/*
 * Request and answer shapes for WORK > Proposals (proposals.ts has the rules).
 */
import { z } from "zod";
import { QUOTE_STATUSES } from "./billing.js";
import { PROPOSAL_SERVICES, proposalWalk } from "./proposals.js";

const text = (max: number) => z.string().trim().max(max).nullable().optional();

/** Save a proposal: who it is for and the walkthrough. Autosave keeps a draft and never refuses an unfinished one. */
export const proposalSaveBody = proposalWalk.extend({
  clientId: z.uuid().nullable().optional(),
  /** A new client: the business, who to talk to and how to reach them. */
  company: text(80),
  contactName: text(120),
  contactPhone: text(30),
  contactEmail: z.email().nullable().optional(),
  address: text(300),
  autosave: z.boolean().default(false),
});
export type ProposalSaveBody = z.infer<typeof proposalSaveBody>;

export const proposalRow = z.object({
  quoteId: z.uuid(),
  number: z.number(),
  business: z.string(),
  contactName: z.string().nullable(),
  service: z.enum(PROPOSAL_SERVICES),
  status: z.enum(QUOTE_STATUSES),
  totalCents: z.number(),
  /** The price is a month (not one time). */
  monthly: z.boolean(),
  archived: z.boolean(),
  createdAt: z.string(),
});
export type ProposalRow = z.infer<typeof proposalRow>;

/** One proposal, to edit it again: the quote's contact and the walkthrough as saved. */
export const proposalDetail = z.object({
  quoteId: z.uuid(),
  number: z.number(),
  status: z.enum(QUOTE_STATUSES),
  building: z.string(),
  clientId: z.uuid().nullable(),
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  contactEmail: z.string().nullable(),
  address: z.string().nullable(),
  walk: proposalWalk,
});
export type ProposalDetail = z.infer<typeof proposalDetail>;

export const proposalSaved = z.object({ quoteId: z.uuid().nullable(), saved: z.boolean() });

/** The proposal part of the client's quote page: the cover, the letter, at a glance, the building and the scope. */
export const publicProposal = z.object({
  serviceWords: z.string(),
  building: z.string(),
  preparedFor: z.string(),
  address: z.string().nullable(),
  date: z.string(),
  greeting: z.string(),
  letter: z.array(z.string()),
  signName: z.string(),
  signTitle: z.string(),
  when: z.string(),
  once: z.boolean(),
  about: z.string(),
  /** What each line item includes, listed under its line (matched by the line's description). */
  included: z.array(z.object({ line: z.string(), items: z.array(z.string()) })),
  scope: z.array(z.object({ label: z.string(), items: z.array(z.string()) })),
  bring: z.array(z.string()),
  insurance: z.string(),
  highlights: z.array(z.string()),
  quality: z.string(),
  terms: z.array(z.string()),
  rating: z.string(),
  reviews: z.array(z.object({ text: z.string(), name: z.string() })),
});
export type PublicProposal = z.infer<typeof publicProposal>;
